import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Button,
  Grid,
  TextField,
  Typography,
  Paper,
  Avatar,
  Stack,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  useTheme,
  Collapse,
  Divider
} from '@mui/material';
import { Edit, Refresh, ExpandMore, ExpandLess } from '@mui/icons-material';
import moment from 'moment';
import axios from '../../resources/axios';
import { handleDefaultErrors } from '../../resources/functions';

interface Admin {
  _id: string;
  login: string;
  firstName?: string;
  lastName?: string;
}

interface EditHistory {
  _id: string;
  admin: string;
  adminLogin: string;
  prevText: string;
  updatedAt: string;
}

interface UserNote {
  _id: string;
  user: string;
  admin?: Admin;
  adminLogin: string;
  city: string;
  text: string;
  createdAt: string;
  updatedAt: string;
  editedBy?: EditHistory[];
}

interface Props {
  userId: string;
}

const UserNotesContent = ({ userId }: Props) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const [notes, setNotes] = useState<UserNote[]>([]);
  const [newNote, setNewNote] = useState<string>('');
  const [loading, setLoading] = useState(false);
  const [editDialog, setEditDialog] = useState<{ open: boolean; note: UserNote | null }>({
    open: false,
    note: null
  });
  const [editText, setEditText] = useState('');
  const [expandedHistory, setExpandedHistory] = useState<{ [key: string]: boolean }>({});

  const getNotes = async () => {
    if (!userId) return;
    
    setLoading(true);
    try {
      const response = await axios.get('/users/note', {
        params: { userId }
      });
      const notesList = response.data?.data?.notes ?? [];
      setNotes(notesList);
    } catch (error) {
      handleDefaultErrors(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (userId) {
      getNotes();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId]);

  const addNewNote = async () => {
    if (!userId || !newNote.trim()) return;
    
    try {
      await axios.post('/users/note', {
        userId,
        text: newNote.trim()
      });
      getNotes();
      setNewNote('');
    } catch (error) {
      handleDefaultErrors(error);
    }
  };

  const openEditDialog = (note: UserNote) => {
    setEditDialog({ open: true, note });
    setEditText(note.text);
  };

  const closeEditDialog = () => {
    setEditDialog({ open: false, note: null });
    setEditText('');
  };

  const editNote = async () => {
    if (!editDialog.note || !editText.trim()) return;

    try {
      await axios.put(`/users/note/${editDialog.note._id}`, {
        text: editText.trim()
      });
      getNotes();
      closeEditDialog();
    } catch (error) {
      handleDefaultErrors(error);
    }
  };

  const toggleHistoryExpand = (noteId: string) => {
    setExpandedHistory(prev => ({
      ...prev,
      [noteId]: !prev[noteId]
    }));
  };

  const getAvatarColor = (name: string) => {
    const colors = [
      '#F44336', '#E91E63', '#9C27B0', '#673AB7', '#3F51B5', 
      '#2196F3', '#03A9F4', '#00BCD4', '#009688', '#4CAF50', 
      '#8BC34A', '#CDDC39', '#FFC107', '#FF9800', '#FF5722'
    ];
    const index = name.charCodeAt(0) % colors.length;
    return colors[index];
  };

  const getAdminName = (note: UserNote) => {
    if (note.admin?.firstName && note.admin?.lastName) {
      return `${note.admin.firstName} ${note.admin.lastName}`;
    }
    if (note.admin?.login) {
      return note.admin.login;
    }
    return note.adminLogin || t('userModal.unknown');
  };

  const getAdminInitial = (note: UserNote) => {
    const name = getAdminName(note);
    return name.charAt(0).toUpperCase();
  };

  return (
    <Box sx={{ p: 3 }}>
      <Grid container spacing={2} alignItems="flex-start" sx={{ mb: 3 }}>
        <Grid item xs={12} sm={9}>
          <TextField
            name='note'
            label={t('userModal.newNote')}
            fullWidth
            multiline
            rows={3}
            value={newNote}
            onChange={e => setNewNote(e.target.value)}
            placeholder={t('userModal.newNotePlaceholder')}
          />
        </Grid>
        <Grid item xs={12} sm={3}>
          <Stack spacing={1}>
            <Button 
              color='primary' 
              onClick={addNewNote}
              fullWidth
              variant="contained"
              disabled={!newNote.trim() || loading}
              sx={{ 
                backgroundColor: '#5073F8',
                '&:hover': { backgroundColor: '#3d5bd1' }
              }}
            >
              {t('userModal.add')}
            </Button>
            <Button 
              color='inherit' 
              onClick={getNotes}
              fullWidth
              variant="outlined"
              startIcon={<Refresh />}
              disabled={loading}
              sx={{
                borderColor: '#878787',
                color: 'text.primary',
                '&:hover': { borderColor: '#5073F8' }
              }}
            >
              {t('userModal.refresh')}
            </Button>
          </Stack>
        </Grid>
      </Grid>

      <Box sx={{ maxHeight: 500, overflowY: 'auto' }}>
        {notes.length === 0 ? (
          <Typography 
            textAlign='center' 
            sx={{ 
              py: 6,
              color: 'text.secondary',
              backgroundColor: theme.palette.mode === 'light' ? '#F5F5F5' : '#2A2A2A',
              borderRadius: 2
            }}
          >
            {t('userModal.noNotes')}
          </Typography>
        ) : (
          <Grid container spacing={2}>
            {notes.map((note) => {
              const adminInitial = getAdminInitial(note);
              const adminName = getAdminName(note);
              const avatarColor = getAvatarColor(adminName);
              const hasEditHistory = note.editedBy && note.editedBy.length > 0;
              const isHistoryExpanded = expandedHistory[note._id];
              
              return (
                <Grid item xs={12} key={note._id}>
                  <Paper 
                    sx={{ 
                      p: 2,
                      border: `1px solid ${theme.palette.mode === 'light' ? 'rgba(0, 0, 0, 0.08)' : 'rgba(255, 255, 255, 0.1)'}`,
                      borderRadius: 2
                    }}
                  >
                    <Stack direction="row" spacing={1.5} alignItems="flex-start">
                      <Avatar
                        sx={{
                          width: 40,
                          height: 40,
                          bgcolor: avatarColor,
                          fontSize: '1rem',
                          fontWeight: 600
                        }}
                      >
                        {adminInitial}
                      </Avatar>
                      <Box sx={{ flex: 1 }}>
                        <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
                          <Typography variant="body2" fontWeight={600}>
                            {adminName}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {moment(note.createdAt).format('DD.MM.YYYY HH:mm')}
                          </Typography>
                          {note.createdAt !== note.updatedAt && (
                            <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                              ({t('userModal.edited')})
                            </Typography>
                          )}
                        </Stack>
                        <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', mb: hasEditHistory ? 1 : 0 }}>
                          {note.text}
                        </Typography>

                        {hasEditHistory && (
                          <>
                            <Button
                              size="small"
                              onClick={() => toggleHistoryExpand(note._id)}
                              endIcon={isHistoryExpanded ? <ExpandLess /> : <ExpandMore />}
                              sx={{ 
                                textTransform: 'none', 
                                fontSize: '0.75rem',
                                color: 'text.secondary',
                                p: 0,
                                minWidth: 'auto'
                              }}
                            >
                              {t('userModal.editHistory', { count: note.editedBy?.length || 0 })}
                            </Button>
                            <Collapse in={isHistoryExpanded}>
                              <Box sx={{ mt: 1, pl: 2, borderLeft: `2px solid ${theme.palette.divider}` }}>
                                {note.editedBy?.map((edit) => (
                                  <Box key={edit._id} sx={{ mb: 1 }}>
                                    <Typography variant="caption" color="text.secondary" display="block">
                                      {edit.adminLogin} • {moment(edit.updatedAt).format('DD.MM.YYYY HH:mm')}
                                    </Typography>
                                    <Typography variant="caption" sx={{ color: 'text.disabled', fontStyle: 'italic' }}>
                                      {t('userModal.prevText')} {edit.prevText}
                                    </Typography>
                                  </Box>
                                ))}
                              </Box>
                            </Collapse>
                          </>
                        )}
                      </Box>
                      <IconButton
                        size="small"
                        onClick={() => openEditDialog(note)}
                        sx={{
                          color: '#5073F8',
                          '&:hover': {
                            backgroundColor: theme.palette.mode === 'light' ? 'rgba(80, 115, 248, 0.08)' : 'rgba(80, 115, 248, 0.16)'
                          }
                        }}
                      >
                        <Edit fontSize="small" />
                      </IconButton>
                    </Stack>
                  </Paper>
                </Grid>
              );
            })}
          </Grid>
        )}
      </Box>

      <Dialog
        open={editDialog.open}
        onClose={closeEditDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>{t('userModal.editNoteTitle')}</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label={t('userModal.noteTextLabel')}
            fullWidth
            multiline
            rows={4}
            value={editText}
            onChange={(e) => setEditText(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={closeEditDialog} sx={{ color: 'text.secondary' }}>
            {t('userModal.cancel')}
          </Button>
          <Button 
            variant="contained"
            onClick={editNote}
            disabled={!editText.trim()}
            sx={{ 
              backgroundColor: '#5073F8',
              '&:hover': { backgroundColor: '#3d5bd1' }
            }}
          >
            {t('userModal.save')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default UserNotesContent;
